import type { PaketPodjetja } from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';

import { api } from '../lib/api';

interface Lastnosti {
  podjetje: string;
  /** Poklicano po shranjevanju, da se seznam poročil osveži. */
  naSpremembo: () => void;
}

/**
 * Paket in moduli podjetja (samo skrbnik).
 *
 * Paket določi, kaj stranka vidi; posamezen modul je mogoče dodati ali
 * odvzeti mimo paketa. Odstopanje od paketa je izpisano, da je vidno, zakaj
 * se podjetje razlikuje od drugih v istem paketu.
 */
export function NastavitevPaketa({ podjetje, naSpremembo }: Lastnosti): JSX.Element {
  const [stanje, setStanje] = useState<PaketPodjetja | null>(null);
  const [paket, setPaket] = useState<string | null>(null);
  const [moduli, setModuli] = useState<Record<string, boolean>>({});
  const [shranjujem, setShranjujem] = useState(false);
  const [sporocilo, setSporocilo] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  const prevzemi = (odgovor: PaketPodjetja): void => {
    setStanje(odgovor);
    setPaket(odgovor.paket);
    setModuli(Object.fromEntries(odgovor.moduli.map((m) => [m.koda, m.vklopljen])));
  };

  /**
   * Ob menjavi paketa kljukice sledijo novemu paketu.
   *
   * Sicer bi stara stanja obvisela kot ročna odstopanja: ob prehodu na
   * napredni paket bi se njegovi dodatni moduli zapisali kot ročno odvzeti.
   * Odstopanja, ki jih admin želi, doda po izbiri paketa.
   */
  const zamenjajPaket = (koda: string | null): void => {
    setPaket(koda);
    setSporocilo(null);
    if (stanje === null) return;
    const izbran = stanje.paketi.find((p) => p.koda === koda);
    setModuli(
      Object.fromEntries(
        stanje.moduli.map((m) => [m.koda, izbran ? izbran.moduli.includes(m.koda) : false]),
      ),
    );
  };

  useEffect(() => {
    api
      .paket(podjetje)
      .then(prevzemi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Paketa ni bilo mogoče prebrati.');
      });
  }, [podjetje]);

  const shrani = (): void => {
    setShranjujem(true);
    setNapaka(null);
    setSporocilo(null);
    api
      .nastaviPaket(podjetje, paket, moduli)
      .then((odgovor) => {
        prevzemi(odgovor);
        setSporocilo('Shranjeno.');
        naSpremembo();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Paketa ni bilo mogoče shraniti.');
      })
      .finally(() => {
        setShranjujem(false);
      });
  };

  if (stanje === null) {
    return (
      <section className="kartica">
        <h2>Paket in moduli</h2>
        {napaka === null ? <p>Berem …</p> : <p className="napaka">{napaka}</p>}
      </section>
    );
  }

  return (
    <section className="kartica">
      <h2>Paket in moduli</h2>
      <p className="namig">
        Paket določi, katera poročila stranka vidi. Posamezen modul lahko dodate ali odvzamete
        mimo paketa; tak modul je spodaj označen kot odstopanje.
      </p>

      <label className="obrazec">
        Paket
        <select
          value={paket ?? ''}
          onChange={(d) => {
            zamenjajPaket(d.target.value === '' ? null : d.target.value);
          }}
        >
          <option value="">brez paketa (stranka ne vidi ničesar)</option>
          {stanje.paketi.map((p) => (
            <option key={p.koda} value={p.koda}>
              {p.naziv}
            </option>
          ))}
        </select>
      </label>

      <ul className="seznam moduli">
        {stanje.moduli.map((m) => {
          const vklopljen = moduli[m.koda] ?? m.vklopljen;
          // Odstopanje se meri od izbranega paketa, ne od shranjenega: admin
          // takoj vidi, kaj bo po shranjevanju veljalo kot ročna sprememba.
          const izbran = stanje.paketi.find((p) => p.koda === paket);
          const vIzbranemPaketu = izbran ? izbran.moduli.includes(m.koda) : false;
          const odstopa = vklopljen !== vIzbranemPaketu;
          return (
            <li key={m.koda}>
              <label className="vrstica-izbire">
                <input
                  type="checkbox"
                  checked={vklopljen}
                  onChange={(d) => {
                    setModuli((prej) => ({ ...prej, [m.koda]: d.target.checked }));
                  }}
                />
                <span>{m.naziv}</span>
              </label>
              <span className="drobno">
                {!m.naVoljo && 'še ni izdelano · '}
                {odstopa ? (vklopljen ? 'dodano mimo paketa' : 'odvzeto iz paketa') : 'po paketu'}
              </span>
            </li>
          );
        })}
      </ul>

      {napaka !== null && <p className="napaka">{napaka}</p>}
      {sporocilo !== null && <p className="uspeh-besedilo drobno">{sporocilo}</p>}
      <button type="button" onClick={shrani} disabled={shranjujem} className="gumb">
        {shranjujem ? 'Shranjujem …' : 'Shrani'}
      </button>
    </section>
  );
}
